import { useNavigate, Link } from 'react-router-dom';
import { UserAvatar } from '../components/UserAvatar';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import {
  CreditCard,
  MapPin,
  Lock,
  HelpCircle,
  MessageSquare,
  Palette,
  Wallet,
  ChevronRight,
  Info,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface MenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  description: string;
}

const MENU_ITEMS: MenuItem[] = [
  { id: 'balance', label: 'Snagg Balance', icon: Wallet, description: 'View your balance, redeem promo codes' },
  { id: 'payment', label: 'Payment Methods', icon: CreditCard, description: 'Manage your cards and payment options' },
  { id: 'addresses', label: 'Shipping Addresses', icon: MapPin, description: 'Manage your delivery addresses' },
  { id: 'display', label: 'Display', icon: Palette, description: 'Customize your theme and colors' },
  { id: 'privacy', label: 'Privacy & Security', icon: Lock, description: 'Manage your account security' },
  { id: 'help', label: 'Help & Support', icon: HelpCircle, description: 'Get help or contact support' },
  ...(import.meta.env.VITE_SENTRY_DSN ? [{ id: 'feedback', label: 'Send Feedback', icon: MessageSquare, description: 'Report bugs or suggest features' }] : []),
  { id: 'app-info', label: 'App Info', icon: Info, description: 'Build version and environment' },
];

export function AccountPage() {
  useDocumentTitle('Account');
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleMenuClick = (itemId: string) => {
    switch (itemId) {
      case 'payment':
        navigate('/payment-methods');
        break;
      case 'balance':
        navigate('/balance');
        break;
      case 'addresses':
        navigate('/addresses');
        break;
      case 'display':
        navigate('/display');
        break;
      case 'privacy':
        navigate('/security');
        break;
      case 'feedback':
        navigate('/feedback');
        break;
      case 'help':
        navigate('/help');
        break;
      case 'app-info':
        navigate('/app-info');
        break;
      default:
        console.log(`Navigate to: ${itemId}`);
    }
  };

  const renderMenuItem = (item: MenuItem) => {
    const Icon = item.icon;
    return (
      <button
        key={item.id}
        style={styles.menuItem}
        onClick={() => handleMenuClick(item.id)}
      >
        <div style={styles.menuIcon}>
          <Icon size={20} color={colors.gray[600]} />
        </div>
        <div style={styles.menuContent}>
          <h3 style={styles.menuLabel}>{item.label}</h3>
          <p style={styles.menuDescription}>{item.description}</p>
        </div>
        <ChevronRight size={20} color={colors.gray[300]} />
      </button>
    );
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <h1 style={styles.title}>Account</h1>

          {/* Profile Card */}
          <div style={styles.profileCard}>
            <div style={styles.avatarContainer}>
              <UserAvatar src={user?.avatarUrl} alt={user?.username ?? 'User'} size={64} />
            </div>
            <div style={styles.profileInfo}>
              <h2 style={styles.profileName}>
                {user?.displayName || user?.username || 'User'}
              </h2>
              <p style={styles.profileUsername}>{user?.username}</p>
              {user?.isSeller && (
                <span style={styles.sellerBadge}>Seller</span>
              )}
            </div>
            <button style={styles.editButton} onClick={() => navigate(`/user/${user?.username}`)}>View Profile</button>
          </div>

          {/* Menu Items */}
          <div style={styles.menuSection}>
            {MENU_ITEMS.map(renderMenuItem)}
          </div>

          <div style={styles.dataDeletion}>
            <p style={styles.dataDeletionText}>
              Want to delete your account or data?{' '}
              <Link to="/data-deletion" style={styles.dataDeletionLink}>Learn how</Link>
            </p>
          </div>

          <button style={styles.logoutButton} onClick={handleLogout}>
            Log Out
          </button>

        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '600px',
    margin: '0 auto',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: '24px',
  },
  profileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    marginBottom: '24px',
  },
  avatarContainer: {
    width: '72px',
    height: '72px',
    flexShrink: 0,
  },
  avatar: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '50%',
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    fontWeight: 600,
    backgroundColor: colors.brand.blue,
    color: colors.white,
    borderRadius: '50%',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: '20px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '2px',
  },
  profileUsername: {
    fontSize: '14px',
    color: colors.gray[600],
    marginBottom: '8px',
  },
  sellerBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.accent.purple,
    backgroundColor: '#EDE9FE',
    borderRadius: '100px',
  },
  editButton: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.brand.blue,
    backgroundColor: colors.brand.blue10,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  menuSection: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: `1px solid ${colors.gray[100]}`,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.2s',
  },
  menuIcon: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.gray[100],
    borderRadius: '10px',
  },
  menuContent: {
    flex: 1,
  },
  menuLabel: {
    fontSize: '15px',
    fontWeight: 500,
    color: colors.ink.black,
    marginBottom: '2px',
  },
  menuDescription: {
    fontSize: '13px',
    color: colors.gray[600],
  },
  dataDeletion: {
    marginTop: '24px',
    textAlign: 'center' as const,
  },
  dataDeletionText: {
    fontSize: '13px',
    color: colors.gray[600],
  },
  dataDeletionLink: {
    color: colors.gray[600],
    textDecoration: 'underline',
    fontWeight: 500,
  },
  logoutButton: {
    display: 'block',
    margin: '24px auto 0',
    padding: '12px 40px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.accent.pink,
    backgroundColor: 'rgba(255, 45, 85, 0.1)',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
  },
};
